// Lane storage: the write path for verification evidence. `SegmentVerifier`
// reports outcomes positionally — {rendition, segmentIndex, hash} — and this
// module stores them exactly as reported, one lane per sealed rendition, with
// no mapping and no information loss. See the "Lanes: results are stored in
// the shape they were produced" section of
// docs/plans/2026-09-07-verification-overlay.md for the full rationale.
//
// Pure. No React, no DOM, no fetch. `composite.ts` (T3) is the read model
// built on top of this; nothing here interprets a LaneSet for display.
import { DURATION_TOLERANCE_SECONDS } from '../SegmentVerifier.js'
import type { SegmentVerificationResult } from '../SegmentVerifier.js'
import type { RenditionVerified } from '../verifyRendition.js'
import type { RootVerified } from '../verifyRoot.js'

export type SliceState = 0 /* unchecked */ | 1 /* verified */ | 2 /* mismatch */

export interface Lane {
  rendition: string
  resolution: string
  bandwidth: number
  /** Cumulative start seconds, length = segment_count. */
  starts: Float64Array
  durations: Float64Array
  states: Uint8Array
  hashes: Map<number, string>
  verifiedCount: number
  /** Pixels this rendition decodes to per second of video — see
   * `pixelsPerSecond`. Zero when its resolution cannot be read. */
  pixelRate: number
}

/** A sealed manifest records a rendition's resolution but not its frame
 * rate. Every ladder this player has been given is 30 frames a second. */
export const ASSUMED_FRAMES_PER_SECOND = 30

/**
 * How many pixels a second of this rendition decodes to: width × height ×
 * frames.
 *
 * AN ESTIMATE, AND THE ONLY ONE IN THE LEDGER. What is checked is bytes; a
 * hash says nothing about pixels. But those bytes are what every one of these
 * pixels is decoded from, so a slice whose bytes matched is a slice whose
 * pixels are the sealed ones — and the pixel figure is the one a viewer can
 * picture (Gavin and Kai, 10 Oct: show both). Zero for a resolution that is
 * not "WIDTHxHEIGHT", which leaves the pixel count out rather than guessing.
 */
export function pixelsPerSecond(resolution: string): number {
  const match = /^(\d+)\s*[x×]\s*(\d+)$/i.exec(resolution.trim())
  if (!match) return 0
  return Number(match[1]) * Number(match[2]) * ASSUMED_FRAMES_PER_SECOND
}

export interface LaneSwitch {
  time: number
  from: string
  to: string
}

/**
 * The first mismatch, whatever shape it arrived in.
 *
 * `rendition` and `index` are OPTIONAL because the source type they come from
 * makes them optional: `SegmentVerificationFailure` documents `segmentIndex`
 * as "absent when the failure is about a whole playlist rather than one
 * segment — a segment-count disagreement, for instance". Such a failure still
 * halts playback and still turns the glyph red; it simply cannot be
 * attributed to one cell, so it marks none. `detail` is always present and is
 * what the modal shows when the rest is absent.
 */
export interface Mismatch {
  rendition?: string
  index?: number
  start?: number
  expectedHash?: string
  actualHash?: string
  detail: string
}

export interface LaneSet {
  lanes: Map<string, Lane>
  /** root.manifest.renditions[0].name */
  canonical: string
  /** The CANONICAL lane's manifest.total_duration, never the max across
   * lanes. */
  totalDuration: number
  aligned: boolean
  active: string | null
  switches: LaneSwitch[]
  /** Sticky: first wins, never replaced. */
  mismatch: Mismatch | null
  /** Bytes hashed and found to match, across every lane. A slice counts once
   * per lane, however often a seek re-fetches it. */
  checkedBytes: number
  /** Seconds of video those matched slices cover, summed the same way. The
   * denominator of "bytes in every second of video". */
  checkedSeconds: number
  /** The pixels those seconds decode to, by each lane's own `pixelRate`. */
  checkedPixels: number
}

/** Cumulative start seconds for a segment list: starts[0] === 0, and
 * starts[i] is the sum of every duration before index i. */
export function cumulativeStarts(durations: number[]): Float64Array {
  const starts = new Float64Array(durations.length)
  let acc = 0
  for (let i = 0; i < durations.length; i++) {
    starts[i] = acc
    acc += durations[i]
  }
  return starts
}

function laneAgreesWithCanonical(canonicalStarts: Float64Array, laneStarts: Float64Array): boolean {
  if (laneStarts.length !== canonicalStarts.length) return false
  for (let i = 0; i < canonicalStarts.length; i++) {
    if (Math.abs(canonicalStarts[i] - laneStarts[i]) > DURATION_TOLERANCE_SECONDS) return false
  }
  return true
}

export function buildLanes(root: RootVerified, renditions: RenditionVerified[]): LaneSet {
  const canonical = root.manifest.renditions[0]?.name ?? ''

  const lanes = new Map<string, Lane>()
  for (const rendition of renditions) {
    const manifest = rendition.manifest
    const durations = manifest.segments.map((segment) => segment.duration)
    lanes.set(manifest.name, {
      rendition: manifest.name,
      resolution: manifest.resolution,
      bandwidth: manifest.bandwidth,
      starts: cumulativeStarts(durations),
      durations: Float64Array.from(durations),
      states: new Uint8Array(durations.length),
      hashes: new Map(),
      verifiedCount: 0,
      pixelRate: pixelsPerSecond(manifest.resolution),
    })
  }

  const canonicalRendition = renditions.find((r) => r.manifest.name === canonical)
  const totalDuration = canonicalRendition?.manifest.total_duration ?? 0
  const canonicalLane = lanes.get(canonical)

  let aligned = true
  if (canonicalLane) {
    for (const lane of lanes.values()) {
      if (lane === canonicalLane) continue
      if (!laneAgreesWithCanonical(canonicalLane.starts, lane.starts)) {
        aligned = false
        break
      }
    }
  }

  return {
    lanes,
    canonical,
    totalDuration,
    aligned,
    active: null,
    switches: [],
    mismatch: null,
    checkedBytes: 0,
    checkedSeconds: 0,
    checkedPixels: 0,
  }
}

function withActive(set: LaneSet, rendition: string, time: number): { active: string | null; switches: LaneSwitch[] } {
  if (set.active === null || set.active === rendition) {
    return { active: rendition, switches: set.switches }
  }
  return {
    active: rendition,
    switches: [...set.switches, { time, from: set.active, to: rendition }],
  }
}

/** Returns the SAME reference when nothing changed. */
export function applyResult(set: LaneSet, result: SegmentVerificationResult): LaneSet {
  if (result.ok) {
    const lane = set.lanes.get(result.rendition)
    if (!lane) return set
    if (result.segmentIndex < 0 || result.segmentIndex >= lane.states.length) return set
    if (lane.states[result.segmentIndex] === 1) return set

    lane.verifiedCount++
    lane.states[result.segmentIndex] = 1
    lane.hashes.set(result.segmentIndex, result.hash)

    const { active, switches } = withActive(set, result.rendition, lane.starts[result.segmentIndex])
    const seconds = lane.durations[result.segmentIndex]
    return {
      ...set,
      active,
      switches,
      checkedBytes: set.checkedBytes + (result.bytes ?? 0),
      checkedSeconds: set.checkedSeconds + seconds,
      checkedPixels: set.checkedPixels + lane.pixelRate * seconds,
    }
  }

  // A fetch or decode error is not a mismatch. Nothing changes.
  if (result.kind === 'network') return set

  // kind === 'mismatch' from here.
  const lane = result.rendition ? set.lanes.get(result.rendition) : undefined
  let active = set.active
  let switches = set.switches
  let start: number | undefined

  if (lane && typeof result.segmentIndex === 'number') {
    if (result.segmentIndex >= 0 && result.segmentIndex < lane.states.length) {
      lane.states[result.segmentIndex] = 2
      start = lane.starts[result.segmentIndex]
      const next = withActive(set, result.rendition as string, start)
      active = next.active
      switches = next.switches
    }
  }

  const mismatch: Mismatch =
    set.mismatch ??
    {
      rendition: result.rendition,
      index: result.segmentIndex,
      start,
      expectedHash: result.expectedHash,
      actualHash: result.actualHash,
      detail: result.detail,
    }

  return { ...set, active, switches, mismatch }
}
